<template>
    <div>
        <div class="card">
            <div
                v-if="header"
                class="card-header d-flex justify-content-between">
                <div style="margin-top: 2px;">
                    <slot></slot>
                </div>
            </div>
            <div class="card-body small">
                <p v-if="!header" class="font-weight-bold mb-2" style="font-size: 1rem;">
                    <slot></slot>
                </p>
                <b-table
                    class="mb-0"
                    :responsive="true"
                    :items="coupons"
                    :fields="fields">
                    <template v-slot:cell(name)="data">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <div>{{ data.item.name }}</div>
                                <div v-if="data.item.campaign" class="small text-muted">{{ data.item.campaign.name }}</div>
                            </div>
                            <b-button
                                v-if="showUseCondition && data.item.doctor_id !== null"
                                variant="yokumiru-blue"
                                size="sm"
                                @click="openCouponUseConditionModal(data.item)">
                                使用条件
                            </b-button>
                        </div>
                    </template>

                    <template v-slot:cell(discount)="data">
                        <div>{{ discount(data.item).toLocaleString() }}円</div>
                    </template>

                    <template v-slot:cell(amount)="data">
                        <div>{{ data.item.amount }}</div>
                    </template>

                    <template v-slot:cell(expired_at)="data">
                        <div v-if="isExpiredNothing(data.item)">
                            <span>無期限</span>
                        </div>
                        <div v-else-if="data.item.expired_at" class="u-palt">
                            <span>{{ expiredDate(data.item) }}&nbsp;（</span><!--
                         --><span :class="expiredDayCSS(data.item)">{{ expiredDay(data.item) }}</span><!--
                         --><span>）</span>
                            <span>{{ expiredTime(data.item) }}</span>
                        </div>
                        <div v-else class="text-muted">未取得</div>
                        <timezone-box-common
                            v-if="timezone && data.item.expired_at && !isExpiredNothing(data.item)"
                            :format="data.item.expired_at"
                            timezone="Asia/Tokyo"
                            :force-display="timezone !== 'Asia/Tokyo'">
                        </timezone-box-common>
                    </template>
                </b-table>
            </div>
        </div>

        <how-to-use-coupon-modal/>
        <coupon-use-condition-modal
            :plan="plan"
            :campaigns="campaigns"
            :timezone="timezone">
        </coupon-use-condition-modal>
    </div>
</template>

<script>
import YokumiruHelper from '../../YokumiruHelper'
import constants from '../../constants'
import moment from "moment-timezone"

export default {
    props: {
        plan: {
            type: Object,
            required: true,
        },
        campaigns: {
            type: Array,
            required: true,
        },
        coupons: {
            type: Array,
            required: true,
        },
        timezone: {
            type: String,
            default: 'Asia/Tokyo',
        },
        header: {
            type: Boolean,
            default: true,
        },
        showAmount: {
            type: Boolean,
            default: false,
        },
        showExpiredAt: {
            type: Boolean,
            default: false,
        },
        showUseCondition: {
            type: Boolean,
            default: false,
        },
    },

    data: function () {
        const d = {
            helper: YokumiruHelper,
            fields: [
                {
                    key: 'name',
                    label: 'クーポン名',
                    sortable: false,
                },
                {
                    key: 'discount',
                    label: '割引額',
                    sortable: false,
                },
            ],
        }

        if (this.showAmount) {
            d.fields.push({
                key: 'amount',
                label: '数量',
                sortable: false,
            })
        }
        if (this.showExpiredAt) {
            d.fields.push({
                key: 'expired_at',
                label: '使用期限',
                sortable: false,
            })
        }

        return d
    },

    methods: {
        /**
         * 割引率・金額計算
         *
         * @param {object} coupon
         * @return {number}
         */
        discount: function (coupon) {
            if (coupon.category === constants.couponCategory.plan) {
                return this.calcDiscount(this.plan.price_subscription, coupon)
            }
            if (coupon.category === constants.couponCategory.consultation) {
                return this.calcDiscount(this.plan.price_consultation, coupon)
            }
        },

        /**
         * 計算処理
         *
         * @param {number} price
         * @param {object} coupon
         * @return {number}
         */
        calcDiscount: function (price, coupon) {
            if (coupon.discount_unit === 'percent') {
                return price * coupon.discount / 100
            }
            else if (coupon.discount_unit === 'static') {
                return coupon.discount
            }
            else if (coupon.discount_unit === 'daily') {
                return coupon.discount
            }
            return 0
        },

        /**
         * クーポン使用期限の無期限判定
         *
         * @param {object} coupon
         * @return {boolean}
         */
        isExpiredNothing: function (coupon) {
            if (coupon.expired_at === '2100-01-01 00:00:00') {
                return true
            }

            const campaign = this.campaigns.find(campaign => campaign.id === coupon.campaign_id)
            if (!campaign) return false

            return campaign.expire_days === null && campaign.expire_day_at === null
        },

        /**
         * クーポン使用期限表記（日付）
         *
         * @param {object} coupon
         * @return {string}
         */
        expiredDate: function (coupon) {
            if (coupon.expired_at) {
                return this.helper.dateForHuman(coupon.expired_at, this.timezone)
            }
            return ''
        },

        /**
         * クーポン使用期限表記（時刻）
         *
         * @param {object} coupon
         * @return {string}
         */
        expiredTime: function (coupon) {
            if (coupon.expired_at) {
                return this.helper.timeForHuman(coupon.expired_at, this.timezone)
            }
            return ''
        },

        /**
         * クーポン使用期限表記（曜日）
         *
         * @param {object} coupon
         * @return {string}
         */
        expiredDay: function (coupon) {
            if (coupon.expired_at) {
                return this.helper.dayForHuman(coupon.expired_at, this.timezone)
            }
            return ''
        },

        /**
         * クーポン使用期限表記（曜日）のCSS
         *
         * @param {object} coupon
         * @return {string}
         */
        expiredDayCSS: function (coupon) {
            let userTime, day
            if (coupon.expired_at) {
                userTime = this.helper.userTime(coupon.expired_at, this.timezone)
                day = userTime.day()
                if (day === 0) return 'text-danger small font-weight-bold'
                if (day === 6) return 'text-yokumiru-blue small font-weight-bold'
            }
            return ''
        },

        /**
         * 使用条件モーダル表示
         *
         * @param {object} coupon
         */
        openCouponUseConditionModal: function (coupon) {
            bus.$emit('open-modal-coupon-use-condition', coupon)
        },
    },
};
</script>
